import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Lightbulb, Microscope, FlaskConical, Leaf, HeartHandshake } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { ProductCard } from "@/components/ProductCard";
import { TrustBar } from "@/components/TrustBar";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { IMG } from "@/data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Collection() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => setProducts(data)).catch(console.error);
  }, []);

  return (
    <div data-testid="collection-page">
      <PageHero
        eyebrow="All Products"
        lines={["Nature's Finest.", "Thoughtfully Curated."]}
        sub="Every Nutribest Collection is built around one promise: premium ingredients, uncompromising quality, and nutrition you can trust."
        image={IMG.heroCollection}
      >
        <a href="#shop" data-testid="hero-explore-collections-button" className="group inline-flex items-center gap-2.5 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold transition-colors duration-300">
          Explore All Collections <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </a>
      </PageHero>

      <section id="shop" className="py-20 sm:py-28 bg-white border-y border-forest/10 scroll-mt-20">
        <div className="container-nb">
          <SectionHead eyebrow="Shop Nutribest" title="Pure. Natural. Thoughtfully crafted." sub="Every product is lab tested, responsibly sourced, and packed with care for your family." />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="container-nb grid lg:grid-cols-[auto_1fr_1fr] gap-10 items-center bg-creamdark/50 border border-forest/10 rounded-3xl p-8 sm:p-12">
          <Reveal>
            <div className="bg-forestdeep grain relative rounded-2xl p-8 text-center w-full lg:w-56">
              <span className="mx-auto w-14 h-14 rounded-full border border-goldlight/40 flex items-center justify-center text-goldlight"><Lightbulb size={24} strokeWidth={1.5} /></span>
              <h3 className="font-serif text-xl text-cream mt-5">The Nutribest<br />Innovation Lab</h3>
              <p className="font-serif italic text-goldlight text-sm mt-3">Always Exploring.<br />Always Improving.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h3 className="font-serif text-2xl sm:text-3xl text-forest">Can't find what you're looking for?</h3>
            <p className="mt-4 text-sm text-mutedgreen leading-relaxed">We are continuously researching new superfoods and natural wellness solutions to bring you the very best of nature.</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2.5 mt-6">
              {[["Research with Purpose", Microscope], ["Ingredient Excellence", Leaf], ["Science Guided", FlaskConical], ["You Focused", HeartHandshake]].map(([label, Icon]) => (
                <span key={label} className="flex items-center gap-2 text-[11px] font-semibold text-forest/80"><Icon size={13} className="text-gold" /> {label}</span>
              ))}
            </div>
            <p className="mt-6 font-serif italic text-forest text-sm">Innovation rooted in nature, guided by science.</p>
          </Reveal>
          <Reveal delay={0.2}>
            <ParallaxImage src={IMG.heroQuality} alt="Nutribest research" className="rounded-2xl h-56 lg:h-64" speed={8} />
          </Reveal>
        </div>
      </section>

      <TrustBar quote={"\u201CIf it\u2019s from Nutribest, I don\u2019t need to worry about quality.\u201D"} />
    </div>
  );
}
